import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from './supabase';

/**
 * After Stripe Checkout for an email campaign, confirm payment (and send if needed).
 * Looks for ?email_campaign_checkout=1&session_id=cs_...
 */
export function useEmailCampaignCheckoutReturn() {
  const location = useLocation();
  const navigate = useNavigate();
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const flag = params.get('email_campaign_checkout');
    const sessionId = params.get('session_id');

    if (flag === 'cancelled') {
      params.delete('email_campaign_checkout');
      params.delete('session_id');
      params.delete('campaign_id');
      const next = params.toString();
      navigate(`${location.pathname}${next ? `?${next}` : ''}`, { replace: true });
      toast('Campaign payment cancelled. You can retry from the campaign.');
      return;
    }

    if (flag !== '1' || !sessionId) return;
    if (handledRef.current === sessionId) return;
    handledRef.current = sessionId;

    void (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('charge-email-campaign', {
          body: { action: 'confirm-checkout', sessionId },
        });
        if (error || !data?.success) {
          console.error('Email campaign checkout confirm failed:', error || data);
          toast.error(data?.error || 'Could not confirm campaign payment');
        } else if (data.intent === 'schedule') {
          toast.success(
            data.alreadyProcessed
              ? 'Campaign payment already recorded'
              : 'Payment received. Your campaign stays scheduled.',
          );
        } else {
          toast.success(
            data.alreadyProcessed
              ? 'Campaign payment already applied'
              : 'Payment received. Sending your campaign…',
          );
        }
      } catch (err) {
        console.error(err);
        toast.error('Could not confirm campaign payment');
      } finally {
        params.delete('email_campaign_checkout');
        params.delete('session_id');
        params.delete('campaign_id');
        const next = params.toString();
        navigate(`${location.pathname}${next ? `?${next}` : ''}`, { replace: true });
      }
    })();
  }, [location.pathname, location.search, navigate]);
}
